/**
 * /r/:stationId?n=nonce — ถ่ายรูป + กรอกชื่อเล่น + ยินยอม PDPA
 *
 * หน้าตาอิงจาก `mockups/player_you_register.html` ซึ่งใช้ภาษาภาพชุดเดียวกับฉาก 0
 * (ดำเกือบสนิท · เขียวมะนาวเป็นสีเน้น · SF Mono ตัวห่าง)
 *
 * PDPA: ต้องติ๊กยินยอมก่อนถึงจะส่งได้ และต้องบอกให้ชัดว่าเก็บอะไร นานเท่าไหร่
 * (ARCHITECTURE.md §5.8)
 */
import { ENV } from "../env.js";
import { newSession, saveSession } from "../session.js";

/** ส่วนที่ใช้จริงของ Turnstile JS API — ไม่ต้องลง @types เพิ่ม */
type TurnstileApi = {
  render: (el: HTMLElement, opts: {
    sitekey: string;
    action?: string;
    appearance?: string;
    "refresh-expired"?: string;
    "expired-callback"?: () => void;
    "error-callback"?: (code?: string) => void;
  }) => string;
  getResponse: (id: string) => string;
  reset: (id: string) => void;
};

const PHOTO_MAX = 4 * 1024 * 1024;
const RETENTION_DAYS = 7;

export function renderRegister(root: HTMLElement, stationId: number, nonce: string): void {
  root.innerHTML = `
    <div class="title">PLAYER YOU</div>
    <div class="kick">BECOME THE CHARACTER</div>

    <div class="lbl">1 · ถ่ายรูปเต็มตัว</div>
    <div class="stagebox">
      <video id="cam" playsinline muted autoplay></video>
      <canvas id="shot" hidden></canvas>
      <div class="meta"><span id="camhint">ยืนให้เห็นเต็มตัวในกรอบ</span><span>STATION ${String(stationId).padStart(2, "0")}</span></div>
    </div>
    <div class="row" style="margin-top:10px">
      <button class="ghost on" id="snap">ถ่ายรูป</button>
      <button class="ghost" id="pick">เลือกจากคลังรูป</button>
      <button class="ghost" id="retake" hidden>ถ่ายใหม่</button>
    </div>
    <input id="file" type="file" accept="image/*" hidden />
    <div class="err" id="camerr"></div>

    <div class="lbl">2 · ชื่อเล่น (ไม่บังคับ)</div>
    <input id="nick" type="text" maxlength="24" placeholder="เช่น AN" autocomplete="off" />

    <div id="tsbox"></div>
    <div class="lbl">3 · ยินยอมให้เก็บรูป</div>
    <div class="card">
      <div class="consent">
        <input type="checkbox" id="consent" />
        <label for="consent">
          ยินยอมให้เก็บรูปถ่ายเพื่อสร้างตัวละครในงานนี้
          <div class="note" style="margin-top:8px">
            เก็บ <b>รูปถ่ายต้นฉบับ</b> ไม่เกิน ${RETENTION_DAYS} วันแล้วลบอัตโนมัติ ·
            เก็บ <b>ตัวละครพิกเซล</b> กับชื่อเล่นไว้จนจบงาน ·
            ขอลบข้อมูลได้ทุกเมื่อที่หน้า “ตัวละครของฉัน”
          </div>
        </label>
      </div>
    </div>

    <div style="margin-top:20px"><button class="primary" id="go" disabled>สร้างตัวละคร</button></div>
    <div class="err" id="err"></div>
    <div class="foot"><span>STATION <b>${String(stationId).padStart(2, "0")}</b></span><span>ถ่ายใหม่ได้ไม่จำกัด</span></div>
  `;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;

  /**
   * Turnstile — ผู้ชมส่วนใหญ่ไม่เห็นอะไรเลย
   *
   * ตั้ง widget เป็นโหมด **managed** คู่กับ `appearance: interaction-only`
   * ไม่ใช่โหมด invisible ทั้งที่โจทย์คือ "มองไม่เห็น" เพราะวัดมาแล้วว่า
   * โหมด invisible เมื่อ Cloudflare สงสัยว่าเป็นบอทจะคืน error 600010
   * แล้ว **ไม่มีทางแก้เลย** ฟอร์มตายสนิท (ทดสอบด้วยเบราว์เซอร์อัตโนมัติ
   * ได้ 600010 ทั้งสามค่าของ appearance) · งานนี้คนดูหลายสิบเครื่องออกเน็ต
   * ผ่าน NAT เดียวกัน โดนสงสัยได้ง่ายมาก
   *
   * managed + interaction-only ให้ผลเหมือนกันสำหรับคนปกติ (ไม่โผล่อะไรเลย)
   * แต่คนที่โดนสงสัยจะได้ช่องติ๊กให้กดผ่านแทนที่จะติดตาย
   *
   * ข้อควรระวังอีกสองอย่าง
   *  1. token ใช้ได้ **ครั้งเดียว** ส่งไม่ผ่านแล้วกดใหม่ต้อง reset ก่อน
   *     ไม่งั้นรอบสองโดนปฏิเสธด้วย timeout-or-duplicate
   *  2. token หมดอายุใน 5 นาที ซึ่งสั้นกว่าเวลาที่คนยืนถ่ายรูปอยู่หน้าฐาน
   *     จึงต้องมี expired-callback ขอใหม่เอง ไม่งั้นกดส่งแล้วเด้ง
   */
  let tsWidgetId: string | null = null;
  let tsError = "";
  const tsBox = $("tsbox");

  const renderTurnstile = (): void => {
    const t = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    if (!t || !ENV.turnstileKey) return;
    tsWidgetId = t.render(tsBox, {
      sitekey: ENV.turnstileKey,
      action: "register",
      appearance: "interaction-only",
      "refresh-expired": "auto",
      "expired-callback": () => { if (tsWidgetId) t.reset(tsWidgetId); },
      "error-callback": (code?: string) => {
        // ต้องเห็นด้วยตา ไม่ใช่เงียบแล้วปุ่มส่งใช้ไม่ได้โดยไม่รู้สาเหตุ
        tsError = code ?? "ไม่ทราบรหัส";
        console.error("[turnstile] widget error", tsError);
      },
    });
  };

  if (ENV.turnstileKey) {
    const sc = document.createElement("script");
    sc.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    sc.async = true;
    sc.defer = true;
    sc.onload = renderTurnstile;
    sc.onerror = () => {
      $("camerr").textContent = "โหลดตัวตรวจสอบไม่ได้ — ลองรีเฟรชหน้า";
    };
    document.head.appendChild(sc);
  }
  const video = $<HTMLVideoElement>("cam");
  const canvas = $<HTMLCanvasElement>("shot");
  const snapBtn = $<HTMLButtonElement>("snap");
  const retakeBtn = $<HTMLButtonElement>("retake");
  const pickBtn = $<HTMLButtonElement>("pick");
  const fileInput = $<HTMLInputElement>("file");
  const goBtn = $<HTMLButtonElement>("go");
  const consent = $<HTMLInputElement>("consent");
  const errEl = $("err");

  let blob: Blob | null = null;
  let stream: MediaStream | null = null;

  const refresh = () => {
    goBtn.disabled = !(blob && consent.checked);
  };
  consent.addEventListener("change", refresh);

  void (async () => {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1080 }, height: { ideal: 1440 } },
        audio: false,
      });
      video.srcObject = stream;
    } catch (e) {
      $("camerr").textContent =
        `เปิดกล้องไม่ได้: ${String(e)} — เปิดสิทธิ์กล้อง หรือกด “เลือกจากคลังรูป” แทนได้`;
      snapBtn.disabled = true;
    }
  })();

  snapBtn.addEventListener("click", () => {
    const w = video.videoWidth || 1080;
    const h = video.videoHeight || 1440;
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d")!.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (b) => {
        blob = b;
        refresh();
      },
      "image/jpeg",
      0.82,
    );
    // หยุดภาพสดไว้ที่รูปที่ถ่าย — ผู้ชมต้องเห็นว่าได้รูปไหนไปก่อนกดส่ง
    showPicked("รูปที่ถ่าย — เห็นเต็มตัวไหม");
  });

  /**
   * แสดงรูปที่เลือกไว้แทนภาพสด — ใช้ร่วมกันทั้งตอนถ่ายและตอนเลือกจากคลัง
   * ต้องหยุด video ด้วย ไม่ใช่แค่ซ่อน เพราะบางเบราว์เซอร์ยังวาดต่อแม้ถูกซ่อน
   */
  const showPicked = (hint: string) => {
    video.pause();
    canvas.hidden = false;
    video.hidden = true;
    snapBtn.hidden = true;
    pickBtn.hidden = true;
    retakeBtn.hidden = false;
    retakeBtn.classList.add("on");
    $("camhint").textContent = hint;
  };

  pickBtn.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      // ย่อด้านยาวสุดเหลือ 1440 — รูปจากมือถือมัก 4000 px ซึ่งเปลืองเวลาอัปโหลด
      // และเกินขนาดที่ระบบสร้างตัวละครใช้จริงอยู่แล้ว
      const k = Math.min(1, 1440 / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * k);
      canvas.height = Math.round(img.height * k);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => { blob = b; refresh(); }, "image/jpeg", 0.82);
      URL.revokeObjectURL(img.src);
      showPicked("รูปที่เลือก — เห็นเต็มตัวไหม");
    };
    img.onerror = () => {
      $("camerr").textContent = "เปิดไฟล์รูปนี้ไม่ได้ ลองเลือกไฟล์อื่น";
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });

  retakeBtn.addEventListener("click", () => {
    blob = null;
    canvas.hidden = true;
    video.hidden = false;
    void video.play();
    snapBtn.hidden = false;
    pickBtn.hidden = false;
    retakeBtn.hidden = true;
    // ล้างค่าไฟล์ ไม่งั้นเลือกไฟล์เดิมซ้ำแล้ว change ไม่ยิง
    fileInput.value = "";
    $("camhint").textContent = "ยืนให้เห็นเต็มตัวในกรอบ";
    refresh();
  });

  goBtn.addEventListener("click", () => {
    void submit();
  });

  async function submit(): Promise<void> {
    if (!blob || !consent.checked) return;
    if (blob.size > PHOTO_MAX) {
      errEl.textContent = "ไฟล์ใหญ่เกิน 4 MB";
      return;
    }
    goBtn.disabled = true;
    goBtn.textContent = "กำลังส่ง…";
    errEl.textContent = "";

    const fd = new FormData();
    fd.set("photo", blob, "photo.jpg");
    fd.set("stationId", String(stationId));
    fd.set("nonce", nonce);
    fd.set("nickname", $<HTMLInputElement>("nick").value.trim());
    fd.set("consent", "1");
    const t = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    const tsToken = tsWidgetId && t ? t.getResponse(tsWidgetId) : "";
    if (ENV.turnstileKey && !tsToken) {
      const t2 = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (tsWidgetId && t2) t2.reset(tsWidgetId);
      errEl.textContent = tsError
        ? `ตัวตรวจสอบมีปัญหา (รหัส ${tsError}) — กดสร้างตัวละครอีกครั้ง ถ้ายังไม่ได้ให้รีเฟรชหน้า`
        : "รอสักครู่ ระบบกำลังตรวจสอบว่าไม่ใช่บอท แล้วกดอีกครั้ง";
      goBtn.disabled = false;
      goBtn.textContent = "สร้างตัวละคร";
      return;
    }
    if (tsToken) fd.set("cf-turnstile-response", tsToken);

    try {
      const res = await fetch(`${ENV.apiBase}/api/register`, { method: "POST", body: fd });
      const body = (await res.json()) as { sessionId?: string; playerId?: string; error?: string };
      if (!res.ok || !body.sessionId || !body.playerId) {
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      stream?.getTracks().forEach((t) => t.stop());
      const s = newSession(
        body.sessionId, body.playerId,
        $<HTMLInputElement>("nick").value.trim() || undefined,
      );
      saveSession(s);
      location.href = "/me";
    } catch (e) {
      errEl.textContent = `ส่งไม่สำเร็จ: ${String(e)}`;
      goBtn.disabled = false;
      goBtn.textContent = "สร้างตัวละคร";
      // token ถูกใช้ไปแล้วไม่ว่าจะผ่านหรือไม่ — ต้องขอใหม่ก่อนกดส่งอีกครั้ง
      const t2 = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (tsWidgetId && t2) t2.reset(tsWidgetId);
    }
  }
}
